import { initializeApp, getApps } from "firebase/app";
import {
  getFirestore, collection, addDoc, getDocs, query, orderBy,
  onSnapshot, serverTimestamp, doc, deleteDoc, setDoc, getDoc, updateDoc, increment,
} from "firebase/firestore";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const app = getApps()[0] || initializeApp(firebaseConfig);
export const db = getFirestore(app);

// ── EXISTING MEDIA ─────────────────────────────────────
export async function addMedia(entry) {
  return addDoc(collection(db, "media"), { ...entry, createdAt: serverTimestamp(), createdAtMs: Date.now() });
}
export async function addNote(entry) {
  return addDoc(collection(db, "notes"), { ...entry, createdAt: serverTimestamp(), createdAtMs: Date.now() });
}
export function subscribeMedia(cb) {
  const q = query(collection(db, "media"), orderBy("createdAtMs", "desc"));
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
}
export function subscribeNotes(cb) {
  const q = query(collection(db, "notes"), orderBy("createdAtMs", "desc"));
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
}
export async function fetchAllMedia() {
  const snap = await getDocs(query(collection(db, "media"), orderBy("createdAtMs", "desc")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export async function fetchAllNotes() {
  const snap = await getDocs(query(collection(db, "notes"), orderBy("createdAtMs", "desc")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export async function deleteMediaById(id) { return deleteDoc(doc(db, "media", id)); }
export async function deleteNoteById(id) { return deleteDoc(doc(db, "notes", id)); }

// ── GUEST PHOTOS ───────────────────────────────────────
export async function addGuestPhoto(entry) {
  return addDoc(collection(db, "guest_photos"), { ...entry, createdAt: serverTimestamp(), createdAtMs: Date.now(), votes: 0 });
}
export function subscribeGuestPhotos(cb) {
  const q = query(collection(db, "guest_photos"), orderBy("createdAtMs", "desc"));
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
}
export async function fetchAllGuestPhotos() {
  const snap = await getDocs(query(collection(db, "guest_photos"), orderBy("createdAtMs", "desc")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export async function deleteGuestPhotoById(id) { return deleteDoc(doc(db, "guest_photos", id)); }

// ── VOTES ──────────────────────────────────────────────
export async function voteForPhoto(mediaId, collectionName, voterName) {
  const voteDocId = `${collectionName}_${mediaId}_${voterName.replace(/\W+/g, "_")}`;
  const voteRef = doc(db, "votes", voteDocId);
  const existing = await getDoc(voteRef);
  if (existing.exists()) return { alreadyVoted: true };
  await setDoc(voteRef, { mediaId, collectionName, voterName, createdAtMs: Date.now() });
  const col = collectionName === "guest_photos" ? "guest_photos" : "media";
  await updateDoc(doc(db, col, mediaId), { votes: increment(1) });
  return { alreadyVoted: false };
}

// ── COUPLE PAGE ────────────────────────────────────────
export async function getCouplePageSettings() {
  const ref = doc(db, "couple_page", "settings");
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null;
}
export async function saveCouplePageSettings(data) {
  return setDoc(doc(db, "couple_page", "settings"), { ...data, updatedAt: serverTimestamp() }, { merge: true });
}
export function subscribeCouplePageSettings(cb) {
  return onSnapshot(doc(db, "couple_page", "settings"), (snap) => cb(snap.exists() ? snap.data() : null));
}

// ── NOTE EDITING ────────────────────────────────────────────────────────────
export async function updateNoteById(id, message) {
  return updateDoc(doc(db, "notes", id), { message, editedAt: serverTimestamp() });
}

// ── MEDIA EDITING ────────────────────────────────────────────────────────────
export async function updateMediaGuestName(id, guestName) {
  return updateDoc(doc(db, "media", id), { guestName: guestName.trim(), editedAt: serverTimestamp() });
}

// ── GUEST NAME EDITING ───────────────────────────────────────────────────────
export async function updateGuestNameById(id, name) {
  return updateDoc(doc(db, "guest_names", id), { name: name.trim(), editedAt: serverTimestamp() });
}

// ── GUEST PHOTO EDITING ───────────────────────────────────────────────────────
export async function updateGuestPhotoGuestName(id, guestName) {
  return updateDoc(doc(db, "guest_photos", id), { guestName: guestName.trim(), editedAt: serverTimestamp() });
}

// ── OUR JOURNEY ────────────────────────────────────────
export async function getJourneySettings() {
  const snap = await getDoc(doc(db, "journey_page", "settings"));
  return snap.exists() ? snap.data() : null;
}
export async function saveJourneySettings(data) {
  return setDoc(doc(db, "journey_page", "settings"), { ...data, updatedAt: serverTimestamp() }, { merge: true });
}
export function subscribeJourneySettings(cb) {
  return onSnapshot(doc(db, "journey_page", "settings"), (snap) => cb(snap.exists() ? snap.data() : null));
}

// ── GUEST NAME REGISTRY (one name per device/guest) ────
export function subscribeGuestNames(cb) {
  const q = query(collection(db, "guest_names"), orderBy("createdAtMs", "desc"));
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
}
export async function registerGuestName(name) {
  const id = name.trim().toLowerCase().replace(/\W+/g, "_");
  const ref = doc(db, "guest_names", id);
  const snap = await getDoc(ref);
  if (snap.exists() && snap.data().name !== name.trim()) return { taken: true };
  await setDoc(ref, { name: name.trim(), createdAtMs: Date.now() }, { merge: true });
  return { taken: false };
}
export async function isNameTaken(name) {
  const id = name.trim().toLowerCase().replace(/\W+/g, "_");
  const snap = await getDoc(doc(db, "guest_names", id));
  return snap.exists();
}

// ── PRESENCE / LIVE ACTIVITY ───────────────────────────
export async function updatePresence(sessionId, data) {
  // data: { name, role, page, pageLabel, lastSeenMs }
  return setDoc(doc(db, "presence", sessionId), {
    ...data,
    lastSeenMs: Date.now(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
}
export async function removePresence(sessionId) {
  return deleteDoc(doc(db, "presence", sessionId));
}
export function subscribePresence(cb) {
  // Only show users active within last 90 seconds
  return onSnapshot(collection(db, "presence"), (snap) => {
    const now = Date.now();
    const active = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((p) => p.lastSeenMs && now - p.lastSeenMs < 90000)
      .sort((a, b) => (b.lastSeenMs || 0) - (a.lastSeenMs || 0));
    cb(active);
  });
}


export async function logLoginSession(entry) {
  // entry: { name, role, deviceInfo }
  return addDoc(collection(db, "login_sessions"), {
    ...entry,
    createdAt: serverTimestamp(),
    createdAtMs: Date.now(),
  });
}
export function subscribeLoginSessions(cb) {
  const q = query(collection(db, "login_sessions"), orderBy("createdAtMs", "desc"));
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
}
export async function deleteLoginSessionById(id) { return deleteDoc(doc(db, "login_sessions", id)); }
export async function deleteAllFromCollection(colName) {
  const snap = await getDocs(collection(db, colName));
  const batches = [];
  snap.docs.forEach(d => batches.push(deleteDoc(doc(db, colName, d.id))));
  await Promise.all(batches);
  return batches.length;
}

// ── FAVOURITES ─────────────────────────────────────────
export async function addFavourite(mediaId, guestName) {
  const id = `${mediaId}_${(guestName || "anon").trim().toLowerCase().replace(/\W+/g, "_")}`;
  await setDoc(doc(db, "favourites", id), {
    mediaId, guestName: guestName || "Anonymous", createdAtMs: Date.now(),
  });
  return id;
}
export async function removeFavourite(mediaId, guestName) {
  const id = `${mediaId}_${(guestName || "anon").trim().toLowerCase().replace(/\W+/g, "_")}`;
  await deleteDoc(doc(db, "favourites", id));
}
export async function isFavourited(mediaId, guestName) {
  const id = `${mediaId}_${(guestName || "anon").trim().toLowerCase().replace(/\W+/g, "_")}`;
  const snap = await getDoc(doc(db, "favourites", id));
  return snap.exists();
}
export function subscribeFavourites(cb) {
  const q = query(collection(db, "favourites"), orderBy("createdAtMs", "desc"));
  return onSnapshot(q, snap => cb(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
}
export function subscribeMyFavourites(guestName, cb) {
  return subscribeFavourites(all =>
    cb(all.filter(f => f.guestName && guestName && f.guestName.trim().toLowerCase() === guestName.trim().toLowerCase()))
  );
}

// ── DJ SONG REQUESTS ───────────────────────────────────
export async function addDJRequest(entry) {
  return addDoc(collection(db, "dj_requests"), { ...entry, createdAt: serverTimestamp(), createdAtMs: Date.now() });
}
export function subscribeDJRequests(cb) {
  const q = query(collection(db, "dj_requests"), orderBy("createdAtMs", "desc"));
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
}
export async function fetchAllDJRequests() {
  const snap = await getDocs(query(collection(db, "dj_requests"), orderBy("createdAtMs", "desc")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export async function deleteDJRequestById(id) {
  return deleteDoc(doc(db, "dj_requests", id));
}
export async function markDJRequestAsPlayed(id) {
  return updateDoc(doc(db, "dj_requests", id), { played: true, playedAt: serverTimestamp(), playedAtMs: Date.now() });
}
export async function unmarkDJRequestAsPlayed(id) {
  return updateDoc(doc(db, "dj_requests", id), { played: false, playedAt: null });
}
